/**
 * MKS-314 follow-up — the single source of truth for showing a CZK aggregate
 * in the instance's reporting currency, and for saying where that number came
 * from.
 *
 * Deliberately the same shape as `utils/localeFormat.ts` and
 * `utils/dateFormat.ts`: ONE mutable module-level value, written once per
 * render by `useReportingFxSync` in `<App>`, read by plain functions. Money is
 * formatted from table cell renderers, chart tick formatters and
 * `formatCurrency` — none of which can call a hook, and threading a rate table
 * through every one of them is the ~100-call-site change MKS-314 exists to
 * avoid repeating.
 *
 * # What converts, and at which rate
 *
 * The stored ledger is CZK by construction
 * (`docs/private/adr/0003-czk-denominated-billing-ledger.md`), so re-denominating is
 * one multiplication — but by WHICH rate is a product decision, not an
 * implementation detail (Petra Holbová, 2026-10-19, MONA-622):
 *
 *   - a figure belonging to one month converts at THAT MONTH'S AVERAGE rate;
 *   - a figure spanning several months is the SUM of its months' conversions,
 *     never one rate applied to one total — hence {@link reportingMoneySum};
 *   - the current, open month uses the latest available daily fixing, so it
 *     moves; a closed month is fixed and does not.
 *
 * ISSUED INVOICES ARE NOT CONVERTED BY ANY OF THIS. They keep their stored
 * amounts and the ČNB rate they were generated with. Nothing in this file is
 * ever written back.
 *
 * # It fails visibly, never silently
 *
 * A month with no usable rate contributes NOTHING to a sum and raises
 * `fxUnavailable`, which every caller renders. Carrying it over at 1:1 would
 * add a CZK figure into a EUR total — a wrong number, where a flagged short
 * one is a visible one.
 */
import { currencySuffix } from './format';
import { formatNumber } from './localeFormat';
import { formatDate } from './dateFormat';

/** How a month's rate was arrived at. Mirrors the backend's `RateBasisKind`. */
export type RateBasisKind = 'monthly_average' | 'latest_daily';

export interface ReportingFxMonth {
  /** 'YYYY-MM'. */
  month: string;
  /** Reporting-currency units per 1 ledger unit (CZK). */
  rate: number;
  basis: RateBasisKind;
  /** The fixing date behind a `latest_daily` rate; null for an average. */
  rateDate: string | null;
}

/** The rate set in force, as the sync hook installs it. */
export interface ReportingFx {
  currency: string;
  ledgerCurrency: string;
  /** `'ecb'` / `'cnb'` — never null here; a non-converting instance installs `null` instead. */
  source: string;
  months: ReportingFxMonth[];
  unavailableMonths: string[];
}

/**
 * `null` — not an identity rate set — is what keeps the flag-off branch
 * byte-identical to today. It is the state while the flag is off, before the
 * query resolves, for a CZK instance reporting in CZK, and in any component
 * test that mounts a page on its own. In all of those, every figure renders as
 * the plain CZK it already is, with no tooltip claiming a source.
 */
let activeReportingFx: ReportingFx | null = null;

/** The rate set in force, or null when nothing is being converted. */
export function getActiveReportingFx(): ReportingFx | null {
  return activeReportingFx;
}

/** Is a reporting-currency conversion in force at all? */
export function hasReportingConversion(): boolean {
  return activeReportingFx !== null;
}

/**
 * Swap the app-wide rate set, or pass `null` to hand every call site back to
 * plain CZK. Called from `useReportingFxSync`, the one place that reads the
 * rates and the MKS-314 release flag.
 *
 * Tests may call it directly, and must reset it to `null` afterwards — the
 * value is module state shared by every test in a file.
 */
export function setActiveReportingFx(fx: ReportingFx | null): void {
  activeReportingFx = fx;
}

/** A figure ready to render: the text, and the tooltip that explains it. */
export interface ReportingMoneyResult {
  text: string;
  /**
   * The rate source, basis and rate. `undefined` when nothing was converted —
   * a figure produced by no rate source must not name one.
   */
  title: string | undefined;
  /** True when a contributing month had no rate, so `text` understates. */
  fxUnavailable: boolean;
  /** The currency `text` is written in. */
  currency: string;
}

export interface ReportingMoneyOptions {
  /** 'YYYY-MM' the figure belongs to. Omitted → the newest month in force. */
  month?: string;
  /** Matches `formatCurrency`'s own flag so a call site can swap 1:1. */
  showDecimals?: boolean;
  /**
   * Exact fraction-digit control, matching `formatMoney`'s third argument.
   * A call site swapping off `formatMoney` MUST pass what it passed there, or
   * the release flag's OFF branch stops being byte-identical: `formatMoney(x,
   * 'CZK', { minimumFractionDigits: 2 })` writes `1 200,00 Kč` where
   * `showDecimals` alone writes `1 200 Kč`.
   */
  numberOptions?: Intl.NumberFormatOptions;
}

/**
 * The currency figures are currently written in — the reporting currency when
 * a conversion is in force, `'CZK'` otherwise.
 *
 * For a surface that builds its own `Intl.NumberFormat` (chart axes, compact
 * tick labels) and so cannot go through {@link reportingMoney}.
 */
export function activeMoneyCurrency(): string {
  return activeReportingFx?.currency ?? 'CZK';
}

/**
 * The multiplier for one month — reporting units per 1 CZK — or `1` when no
 * conversion is in force.
 *
 * For a surface that has to convert a SERIES before handing it to a chart,
 * where converting each rendered label separately would leave the bars and
 * the axis disagreeing. A month with no rate returns `null`, which the caller
 * must surface rather than silently treat as 1.
 */
export function reportingRateForMonth(month: string): number | null {
  const fx = activeReportingFx;
  if (!fx) return 1;
  return fx.months.find((entry) => entry.month === month)?.rate ?? null;
}

/**
 * The multiplier for a figure with no month of its own — a configured annual
 * target, a lifetime total — at the newest rate in force.
 *
 * Deliberately separate from {@link reportingRateForMonth} so the two are
 * never confused at a call site: this one is explicitly the "no month to read"
 * case, and a reviewer can grep for it.
 */
export function reportingRateLatest(): number | null {
  const fx = activeReportingFx;
  if (!fx) return 1;
  return newestMonth(fx)?.rate ?? null;
}

/** Human label for a rate source, kept in step with `fxRateSourceLabel`. */
function sourceLabel(source: string): string {
  if (source === 'ecb') return 'ECB';
  if (source === 'cnb') return 'ČNB';
  return source;
}

/** 'YYYY-MM' → `10/2026`, the form the rate basis is quoted in. */
export function monthLabel(month: string): string {
  const [year, index] = month.split('-');
  return `${index}/${year}`;
}

/** The newest month in force — what a figure with no month of its own uses. */
function newestMonth(fx: ReportingFx): ReportingFxMonth | undefined {
  let newest: ReportingFxMonth | undefined;
  for (const entry of fx.months) {
    if (!newest || entry.month > newest.month) newest = entry;
  }
  return newest;
}

function formatAmount(
  value: number,
  currency: string,
  showDecimals: boolean,
  numberOptions?: Intl.NumberFormatOptions
): string {
  const hasDecimals = value % 1 !== 0;
  return `${formatNumber(
    value,
    numberOptions ?? {
      minimumFractionDigits: showDecimals && hasDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    }
  )} ${currencySuffix(currency)}`;
}

/**
 * The tooltip for one month's rate — `ECB monthly average 10/2026:
 * 1 EUR = 24,31 Kč`.
 *
 * The rate is quoted as one REPORTING unit in ledger units, which is the
 * direction a person reads a rate in ("one euro is twenty-four crowns"), and
 * the inverse of the `rate` field, which is what the arithmetic needs.
 */
function monthTooltip(fx: ReportingFx, entry: ReportingFxMonth): string {
  const inverse = entry.rate > 0 ? 1 / entry.rate : 0;
  const quote = `1 ${fx.currency} = ${formatNumber(inverse, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })} ${currencySuffix(fx.ledgerCurrency)}`;

  const basis =
    entry.basis === 'monthly_average'
      ? `monthly average ${monthLabel(entry.month)}`
      : `rate of ${entry.rateDate ? formatDate(entry.rateDate) : monthLabel(entry.month)}`;

  return `${sourceLabel(fx.source)} ${basis}: ${quote}`;
}

/** The tooltip for a figure summed across several months. */
function sumTooltip(fx: ReportingFx, used: ReportingFxMonth[]): string {
  const first = used[0]!;
  const last = used[used.length - 1]!;
  const span = `${monthLabel(first.month)}–${monthLabel(last.month)}`;
  const open = used.some((entry) => entry.basis === 'latest_daily');

  return (
    `${sourceLabel(fx.source)} monthly averages, sum of monthly conversions (${span})` +
    (open ? '; the open month at the latest fixing' : '')
  );
}

const INCOMPLETE_NOTE = ' — some months had no rate, so this total is incomplete';

/**
 * One CZK figure in the reporting currency.
 *
 * With no conversion in force this is exactly `formatCurrency(value,
 * showDecimals)` and no tooltip, which is what keeps the flag-off branch
 * unchanged.
 */
export function reportingMoney(
  value: number,
  options: ReportingMoneyOptions = {}
): ReportingMoneyResult {
  const { month, showDecimals = true, numberOptions } = options;
  const fx = activeReportingFx;

  if (!fx) {
    return {
      text: formatAmount(value, 'CZK', showDecimals, numberOptions),
      title: undefined,
      fxUnavailable: false,
      currency: 'CZK',
    };
  }

  const entry = month ? fx.months.find((m) => m.month === month) : newestMonth(fx);
  if (!entry) {
    // No rate for this figure's month. Showing the CZK number labelled in the
    // reporting currency would be a wrong number, so the ledger figure is
    // shown as the ledger figure and the tooltip says why.
    return {
      text: formatAmount(value, fx.ledgerCurrency, showDecimals, numberOptions),
      title: `No ${sourceLabel(fx.source)} rate available${
        month ? ` for ${monthLabel(month)}` : ''
      } — shown in ${fx.ledgerCurrency}`,
      fxUnavailable: true,
      currency: fx.ledgerCurrency,
    };
  }

  return {
    text: formatAmount(value * entry.rate, fx.currency, showDecimals, numberOptions),
    title: monthTooltip(fx, entry),
    fxUnavailable: false,
    currency: fx.currency,
  };
}

/**
 * A figure spanning several months: each month at its own rate, then summed.
 *
 * This is the decision's "sum the months for longer periods", and the reason
 * a quarter total is not `reportingMoney(quarterTotal)` — one rate on one
 * total would quietly disagree with the three monthly figures above it.
 */
export function reportingMoneySum(
  parts: ReadonlyArray<{ month: string; amount: number }>,
  options: Omit<ReportingMoneyOptions, 'month'> = {}
): ReportingMoneyResult {
  const { showDecimals = true, numberOptions } = options;
  const fx = activeReportingFx;

  const ledgerTotal = parts.reduce((sum, part) => sum + part.amount, 0);
  if (!fx) {
    return {
      text: formatAmount(ledgerTotal, 'CZK', showDecimals, numberOptions),
      title: undefined,
      fxUnavailable: false,
      currency: 'CZK',
    };
  }

  const byMonth = new Map(fx.months.map((entry) => [entry.month, entry]));
  // Two sets, because "a rate was found" and "a rate moved the total" are
  // different questions. A zero-amount month must not widen the tooltip's
  // span to months that contributed nothing, but a total that is genuinely
  // zero must still say which rate it was read at rather than claim no rate
  // was available.
  const matched = new Map<string, ReportingFxMonth>();
  const moved = new Map<string, ReportingFxMonth>();
  let total = 0;
  let fxUnavailable = false;

  for (const part of parts) {
    const entry = byMonth.get(part.month);
    if (!entry) {
      // A month holding no money needs no rate, so it is not worth flagging.
      if (part.amount !== 0) fxUnavailable = true;
      continue;
    }
    total += part.amount * entry.rate;
    matched.set(entry.month, entry);
    if (part.amount !== 0) moved.set(entry.month, entry);
  }

  const byMonthAsc = (a: ReportingFxMonth, b: ReportingFxMonth) =>
    a.month.localeCompare(b.month);
  const contributing = [...(moved.size > 0 ? moved : matched).values()].sort(byMonthAsc);
  if (contributing.length === 0) {
    return {
      text: formatAmount(ledgerTotal, fx.ledgerCurrency, showDecimals, numberOptions),
      title: `No ${sourceLabel(fx.source)} rate available — shown in ${fx.ledgerCurrency}`,
      fxUnavailable: true,
      currency: fx.ledgerCurrency,
    };
  }

  const title =
    contributing.length === 1
      ? monthTooltip(fx, contributing[0]!)
      : sumTooltip(fx, contributing);

  return {
    text: formatAmount(total, fx.currency, showDecimals, numberOptions),
    title: fxUnavailable ? title + INCOMPLETE_NOTE : title,
    fxUnavailable,
    currency: fx.currency,
  };
}
